import { Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  ComparisonResult,
  ComparisonStatCard,
  computeComparison,
  formatComparisonNet,
  getComparisonPolarityClass,
} from '@/modules/common/components/molecules/stat-card';

import { OverheadsSummary } from '../../types/overhead';
import { formatOverheadAmount } from './overheads-summary-card';

/**
 * Income/cost comparison for the current summary: totals across statuses
 * (income = pending + invoiced, cost = planned + paid). The card and the
 * compact Net chip both read from this.
 */
export const computeOverheadsAnalysis = (summary: OverheadsSummary): ComparisonResult =>
  computeComparison(
    summary.incomePending.amount + summary.incomeInvoiced.amount,
    summary.costPlanned.amount + summary.costPaid.amount
  );

/** "+150 USD" / "-70 USD" — net keeps an explicit sign for positive values. */
export const formatOverheadNetAmount = (net: number, currencyCode: string): string =>
  formatComparisonNet(net, amount => formatOverheadAmount(amount, currencyCode));

export const getNetPolarityClass = getComparisonPolarityClass;

interface OverheadsSummaryAnalysisCardProps {
  summary: OverheadsSummary;
}

/**
 * First summary card: income vs cost for the current period/filters, built on
 * the shared `ComparisonStatCard`. Compares the TOTALS of the two neighbor
 * cards — all income statuses vs all cost statuses.
 */
export const OverheadsSummaryAnalysisCard = ({ summary }: OverheadsSummaryAnalysisCardProps) => {
  const { t } = useTranslation(['authenticated.overheads', 'common']);

  return (
    <ComparisonStatCard
      icon={<Scale className="h-4 w-4" />}
      title={t('summary.analysis.title')}
      infoTooltip={t('summary.analysis.tooltip')}
      positive={{
        label: t('summary.analysis.income'),
        amount: summary.incomePending.amount + summary.incomeInvoiced.amount,
      }}
      negative={{
        label: t('summary.analysis.cost'),
        amount: summary.costPlanned.amount + summary.costPaid.amount,
      }}
      netLabel={t('summary.analysis.net')}
      marginLabel={t('summary.analysis.margin')}
      noValueLabel={t('common:table.noValue')}
      formatAmount={amount => formatOverheadAmount(amount, summary.currencyCode)}
    />
  );
};
